import { sendToApi } from 'api/client'
import { AnonymousUser, Auth } from 'types'
import { Response } from 'request-dot-js'
import { FlaggedFeature } from 'hooks/useFlipperFlag'

export interface SuccessResponse {
  success: true
}
export interface ErrorResponse {
  success: false
  error: string
}
export interface TokenVerificationResponse {
  success: boolean
  email: string
  user_exists: boolean
  is_shell_user: boolean
  msg: string
}

type ConnectSlackResponse = SuccessResponse | ErrorResponse

export type DiscoverySource =
  | 'search_engine'
  | 'community'
  | 'product_hunt'
  | 'friend'
  | 'newsletter'
  | 'social_media'
  | 'webinar'
  | 'facebook'
  | 'instagram'
  | 'other'

export interface UserMetrics {
  docs_created_week: number
  docs_created_all_time: number
  docs_shared_week: number
  comments_responded_to: number
  tasks_responded_to: number
  reviews_responded_to: number
  comment_reactions: number
  comment_upvotes: number
}

export interface RegistrationData {
  demo_required: boolean
  demo_completed_at: string | null
  demo_scheduled_at: string | null
  trial_or_demo_required: boolean | null
  trial_required: boolean
  use_cases: string[]
  user_role: string
  discovery_source: DiscoverySource
  team_size: string
  signup_reason: string
  business_type: string
  marketing_budget: string
  user_id: number
  signed_up_at: string | null
  stale: boolean
  utm_source: string | null
  marketing_experience_level: number | null
  content_creation_category: string | null
  content_creation_subcategory: string | null
  is_mobile: boolean | null
}

export interface RegistrationDataRecord {
  key: string
  title: string
  description: string
}

export interface RegistrationDataRecords {
  user_roles: RegistrationDataRecord[]
  use_cases: RegistrationDataRecord[]
}

export const UserClient = {
  authenticateAnonymousUser: () => {
    return sendToApi<{ anonymous_user: AnonymousUser }>(`/anonymous_users`, { method: 'POST' })
  },
  authenticateUnregisteredUser: (email: string, token: string) => {
    return sendToApi<{ unregistered_user: Auth }>('/unregistered_user_sessions', {
      method: 'POST',
      body: { email, token },
    })
  },
  connectSlack: (code: string) => {
    return sendToApi<ConnectSlackResponse>('/slack/connect', {
      method: 'POST',
      body: { code },
    })
  },
  verifyUserExists: (
    email: string,
    redirectTo?: string,
    checkUserExists?: boolean,
    isAutopilot?: boolean,
    workspaceInviteToken?: string,
  ) => {
    return sendToApi('/verify_user_email', {
      method: 'GET',
      params: {
        email,
        redirect_to: redirectTo,
        check_user_exists: checkUserExists,
        is_autopilot: isAutopilot,
        workspace_invite_token: workspaceInviteToken,
      },
    })
  },
  verifyRegistrationToken: async (token: string) => {
    return sendToApi<TokenVerificationResponse>('/verify_registration_token', {
      method: 'GET',
      params: { token },
    })
  },
  verifyEmailConfirmationCode: async (email: string, code: string) => {
    return sendToApi('/verify_confirmation_code', {
      method: 'GET',
      params: { email, code },
    })
  },

  registerUser: (
    firstName: string,
    lastName: string,
    password: string,
    token?: string,
    code?: string,
    email?: string,
    workspaceInviteToken?: string,
    redirectAfterSignup?: string,
  ) => {
    return sendToApi('/register_user', {
      method: 'POST',
      body: {
        token,
        email,
        code,
        firstName,
        lastName,
        password,
        workspaceInviteToken,
        redirectAfterSignup,
      },
    })
  },

  update: (firstName: string, lastName: string) => {
    return sendToApi('/update', {
      method: 'PUT',
      body: {
        first_name: firstName,
        last_name: lastName,
      },
    })
  },

  getRegistrationDataRecords: () => sendToApi<RegistrationDataRecords>('/registration_data'),

  getRegistrationData: () => {
    return sendToApi<{ user_registration_data: RegistrationData | null }>('/registration_data/1')
  },

  registrationData: ({
    signupReason,
    businessType,
    marketingBudget,
    useCases,
    discoverySource,
    teamSize,
    trialRequired,
    demoRequired,
    trialOrDemoRequired,
    utm_campaign,
    utm_content,
    utm_medium,
    utm_source,
    utm_term,
    referral_page,
    referrer,
    marketingExperienceLevel,
    contentCreationCategory,
    contentCreationSubcategory,
    isMobile,
  }: {
    signupReason?: string
    businessType?: string
    marketingBudget?: string
    useCases?: string[]
    discoverySource?: string
    teamSize?: string
    trialRequired?: boolean
    demoRequired?: boolean
    trialOrDemoRequired?: boolean
    utm_campaign?: string | null
    utm_content?: string | null
    utm_medium?: string | null
    utm_source?: string | null
    utm_term?: string | null
    referral_page?: string | null
    referrer?: string | null
    marketingExperienceLevel?: number
    contentCreationCategory?: string
    contentCreationSubcategory?: string
    isMobile?: boolean
  }) => {
    return sendToApi<{ user_registration_data: RegistrationData }>('/registration_data', {
      method: 'POST',
      body: {
        signupReason,
        businessType,
        marketingBudget,
        useCases,
        discoverySource,
        teamSize,
        trialRequired,
        demoRequired,
        trialOrDemoRequired,
        utm_campaign,
        utm_content,
        utm_medium,
        utm_source,
        utm_term,
        referral_page,
        referrer,
        marketingExperienceLevel,
        contentCreationCategory,
        contentCreationSubcategory,
        isMobile,
      },
    })
  },

  updateRegistrationData: ({ demoScheduledAt }: { demoScheduledAt: Date }) => {
    return sendToApi('/registration_data/1', {
      method: 'PUT',
      body: { registration_data: { demo_scheduled_at: demoScheduledAt } },
    })
  },

  sendEmail: (
    email: string,
    email_type: string,
    redirect_to?: string,
    workspace_invite_token?: string,
  ): Promise<Response<any, any>> => {
    return sendToApi<{ data: { success: boolean; msg?: string } }>('/send_registration_email', {
      method: 'POST',
      body: { email, email_type, redirect_to, workspace_invite_token },
    })
  },
  sendAutopilotDesktopLinkEmail: (desktopPath: string) => {
    return sendToApi<{ data: { success: boolean; msg?: string } }>('/send_autopilot_desktop_link_email', {
      method: 'POST',
      body: { redirect_to: desktopPath },
    })
  },
  sendMobileAutopilotDesktopLinkEmail: (desktopPath: string) => {
    return sendToApi<{ data: { success: boolean; msg?: string } }>('/send_ios_autopilot_desktop_link_email', {
      method: 'POST',
      body: { redirect_to: desktopPath },
    })
  },
  addToWorkspace: (workspaceInviteToken: string) => {
    return sendToApi<{ data: { success: boolean; msg?: string } }>('/add_to_workspace', {
      method: 'POST',
      body: { workspace_invite_token: workspaceInviteToken },
    })
  },
  lastWorkspaceId: async () => {
    return sendToApi<{ last_workspace_id: number }>(`/users/last_workspace_id`, {
      method: 'GET',
    })
  },
  getFeatures: () => {
    return sendToApi<FlaggedFeature[]>('/features', {
      method: 'GET',
    })
  },
  getMetrics: async () => {
    return sendToApi<{ user: UserMetrics }>(`/users/metrics`, {
      method: 'GET',
    })
  },
  getCreatedArticlesCount: async () => {
    return sendToApi<{ created_articles_count: number }>(`/users/created_articles_count`, {
      method: 'GET',
    })
  },
  getInitialArticle: async () => {
    return sendToApi<{ url: string | null }>(`/users/initial_article`, {
      method: 'GET',
    })
  },
  getIsAlmanacPrivateMember: async () => {
    return sendToApi<{ almanac_private_member: boolean }>(`/users/almanac_private_member`, {
      method: 'GET',
    })
  },
  completeSignup: async () => {
    return sendToApi(`/users/complete_signup`, { method: 'POST' })
  },
  deactivate: async () => {
    return sendToApi(`/users/deactivate`, { method: 'POST' })
  },
}
